import dayjs from 'dayjs';

/**
 * Format time string (e.g. HH:mm:ss)
 */
export const formatTimeStr = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

/**
 * Format date string (DD/MM/YYYY)
 */
export const formatDateStr = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB');
};

/**
 * All 6 Supported Attendance Statuses with Codes & Descriptions
 */
export const ATTENDANCE_STATUSES = [
  { value: 'PRESENT', code: 'P', label: 'P - Present', color: 'success', tagColor: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', desc: 'Present / Standard shift completed on time' },
  { value: 'ABSENT', code: 'A', label: 'A - Absent', color: 'error', tagColor: '#ff4d4f', bg: '#fff1f0', border: '#ffa39e', desc: 'Absent / No attendance recorded' },
  { value: 'HALF_DAY', code: 'HD', label: 'HD - Half Day', color: 'gold', tagColor: '#faad14', bg: '#fffbe6', border: '#ffe58f', desc: 'Half Day / Worked partial hours' },
  { value: 'OVERTIME', code: 'OT', label: 'OT - Overtime', color: 'volcano', tagColor: '#fa541c', bg: '#fff2e8', border: '#ffbb96', desc: 'Overtime / Worked extra hours beyond shift' },
  { value: 'LATE', code: 'L', label: 'L - Late', color: 'orange', tagColor: '#d46b08', bg: '#fff7e6', border: '#ffd591', desc: 'Late / Checked in after login window' },
  { value: 'LEAVE_EARLY', code: 'LE', label: 'LE - Leave Early', color: 'cyan', tagColor: '#13c2c2', bg: '#e6fffb', border: '#87e8de', desc: 'Leave Early / Checked out before logout window' }
];

/**
 * Normalize raw status string to standard status object
 */
export const normalizeStatusCode = (status) => {
  const s = String(status || '').trim().toUpperCase();
  if (s === 'P' || s === 'PRESENT') return { status: 'PRESENT', code: 'P', label: 'P - Present' };
  if (s === 'A' || s === 'ABSENT') return { status: 'ABSENT', code: 'A', label: 'A - Absent' };
  if (s === 'HD' || s === 'HALF_DAY' || s === 'HALF DAY') return { status: 'HALF_DAY', code: 'HD', label: 'HD - Half Day' };
  if (s === 'OT' || s === 'OVERTIME') return { status: 'OVERTIME', code: 'OT', label: 'OT - Overtime' };
  if (s === 'L' || s === 'LATE') return { status: 'LATE', code: 'L', label: 'L - Late' };
  if (s === 'LE' || s === 'LEAVE_EARLY' || s === 'LEAVE EARLY' || s === 'ON_LEAVE' || s === 'ON LEAVE' || s === 'LEAVE') return { status: 'LEAVE_EARLY', code: 'LE', label: 'LE - Leave Early' };
  return { status: s || 'PENDING', code: s || '-', label: s || 'Pending' };
};

/**
 * Attendance status color tags & descriptions
 */
export const getStatusTagColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PRESENT' || s === 'P') return 'success';
  if (s === 'ABSENT' || s === 'A') return 'error';
  if (s === 'HALF_DAY' || s === 'HALF DAY' || s === 'HD') return 'gold';
  if (s === 'OVERTIME' || s === 'OT') return 'volcano';
  if (s === 'LATE' || s === 'L') return 'orange';
  if (s === 'LEAVE_EARLY' || s === 'LEAVE EARLY' || s === 'LE' || s === 'ON_LEAVE' || s === 'ON LEAVE' || s === 'LEAVE') return 'cyan';
  if (s === 'HOLIDAY' || s === 'H') return 'purple';
  return 'default';
};

/**
 * Matrix Day badge config
 */
export const getMatrixBadgeConfig = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PRESENT' || s === 'P') {
    return { text: 'P', color: '#52c41a', title: 'Present', bg: '#f6ffed', border: '#b7eb8f' };
  }
  if (s === 'ABSENT' || s === 'A') {
    return { text: 'A', color: '#ff4d4f', title: 'Absent', bg: '#fff1f0', border: '#ffa39e' };
  }
  if (s === 'HALF_DAY' || s === 'HALF DAY' || s === 'HD') {
    return { text: 'HD', color: '#faad14', title: 'Half Day', bg: '#fffbe6', border: '#ffe58f' };
  }
  if (s === 'OVERTIME' || s === 'OT') {
    return { text: 'OT', color: '#fa541c', title: 'Overtime', bg: '#fff2e8', border: '#ffbb96' };
  }
  if (s === 'LATE' || s === 'L') {
    return { text: 'L', color: '#d46b08', title: 'Late', bg: '#fff7e6', border: '#ffd591' };
  }
  if (s === 'LEAVE_EARLY' || s === 'LEAVE EARLY' || s === 'LE' || s === 'ON_LEAVE' || s === 'ON LEAVE' || s === 'LEAVE') {
    return { text: 'LE', color: '#13c2c2', title: 'Leave Early', bg: '#e6fffb', border: '#87e8de' };
  }
  if (s === 'HOLIDAY' || s === 'H') {
    return { text: 'H', color: '#722ed1', title: 'Holiday', bg: '#f9f0ff', border: '#d3adf7' };
  }
  return { text: '-', color: '#bfbfbf', title: 'No Record', bg: 'transparent', border: 'transparent' };
};

/**
 * Match a shift rule day_type against day of week (0=Sun, 1=Mon, ..., 6=Sat)
 */
export const matchDayRule = (ruleDayType, targetDayIndex) => {
  const dt = String(ruleDayType || 'ALL').trim().toUpperCase();
  if (dt === 'ALL') return true;
  if (dt === 'WEEKDAY' && targetDayIndex >= 1 && targetDayIndex <= 5) return true;
  if (dt === 'WEEKEND' && (targetDayIndex === 0 || targetDayIndex === 6)) return true;
  if (dt === 'SATURDAY' && targetDayIndex === 6) return true;
  if (dt === 'SUNDAY' && targetDayIndex === 0) return true;

  const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  const dayShorts = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const targetName = dayNames[targetDayIndex];
  const targetShort = dayShorts[targetDayIndex];

  const parts = dt.split(',').map(s => s.trim().toUpperCase());
  return parts.some(p => (
    p === targetName ||
    p === targetShort ||
    (p === 'WEEKDAY' && targetDayIndex >= 1 && targetDayIndex <= 5) ||
    (p === 'SATURDAY' && targetDayIndex === 6) ||
    (p === 'SUNDAY' && targetDayIndex === 0) ||
    p === 'ALL'
  ));
};

/**
 * Client-side timing rule evaluator for automatic status suggestion
 */
export const calculateTimingStatusClient = (
  checkInTime,
  checkOutTime,
  personType = 'STAFF',
  shiftSettings = null,
  dateOrDay = null,
  combination = 'ALL',
  staffId = null,
  candidateId = null
) => {
  if (!checkInTime && !checkOutTime) {
    return { status: 'ABSENT', code: 'A', label: 'A - Absent' };
  }

  let targetDayIndex = 1;
  let targetDateStr = null;
  let isSat = false;
  if (dateOrDay) {
    const d = dayjs(dateOrDay);
    if (d.isValid()) {
      targetDayIndex = d.day();
      isSat = targetDayIndex === 6;
      targetDateStr = d.format('YYYY-MM-DD');
    }
  }
  const comb = (combination && combination !== 'all') ? combination : 'ALL';
  const sId = staffId ? Number(staffId) : null;
  const cId = candidateId ? Number(candidateId) : null;

  let config = null;
  if (Array.isArray(shiftSettings)) {
    // 1. Exact Date-wise matches
    if (targetDateStr) {
      if (comb !== 'ALL' && (sId || cId)) {
        config = shiftSettings.find(s => s.person_type === personType && s.schedule_type === 'DATE' && s.specific_date === targetDateStr && ((sId && Number(s.staff_id) === sId) || (cId && Number(s.candidate_id) === cId)) && s.combination === comb);
      }
      if (!config && (sId || cId)) {
        config = shiftSettings.find(s => s.person_type === personType && s.schedule_type === 'DATE' && s.specific_date === targetDateStr && ((sId && Number(s.staff_id) === sId) || (cId && Number(s.candidate_id) === cId)));
      }
      if (!config && comb !== 'ALL') {
        config = shiftSettings.find(s => s.person_type === personType && s.schedule_type === 'DATE' && s.specific_date === targetDateStr && !s.staff_id && !s.candidate_id && s.combination === comb);
      }
      if (!config) {
        config = shiftSettings.find(s => s.person_type === personType && s.schedule_type === 'DATE' && s.specific_date === targetDateStr && !s.staff_id && !s.candidate_id);
      }
    }

    // 2. Individual person day-wise match
    if (!config && (sId || cId)) {
      if (comb !== 'ALL') {
        config = shiftSettings.find(s => s.person_type === personType && s.schedule_type !== 'DATE' && ((sId && Number(s.staff_id) === sId) || (cId && Number(s.candidate_id) === cId)) && s.combination === comb && matchDayRule(s.day_type, targetDayIndex));
      }
      if (!config) {
        config = shiftSettings.find(s => s.person_type === personType && s.schedule_type !== 'DATE' && ((sId && Number(s.staff_id) === sId) || (cId && Number(s.candidate_id) === cId)) && matchDayRule(s.day_type, targetDayIndex));
      }
      if (!config) {
        config = shiftSettings.find(s => s.person_type === personType && s.schedule_type !== 'DATE' && ((sId && Number(s.staff_id) === sId) || (cId && Number(s.candidate_id) === cId)));
      }
    }

    // 3. Exact combination match + day match
    if (!config && comb !== 'ALL') {
      config = shiftSettings.find(s => s.person_type === personType && s.schedule_type !== 'DATE' && !s.staff_id && !s.candidate_id && s.combination === comb && matchDayRule(s.day_type, targetDayIndex));
    }
    // 4. Combination match + all days
    if (!config && comb !== 'ALL') {
      config = shiftSettings.find(s => s.person_type === personType && s.schedule_type !== 'DATE' && !s.staff_id && !s.candidate_id && s.combination === comb && (s.day_type === 'ALL' || !s.day_type));
    }
    // 5. Default combination 'ALL' + day match
    if (!config) {
      config = shiftSettings.find(s => s.person_type === personType && s.schedule_type !== 'DATE' && !s.staff_id && !s.candidate_id && (s.combination === 'ALL' || !s.combination) && matchDayRule(s.day_type, targetDayIndex));
    }
    // 6. Default combination 'ALL' + all days
    if (!config) {
      config = shiftSettings.find(s => s.person_type === personType && s.schedule_type !== 'DATE' && !s.staff_id && !s.candidate_id && (s.combination === 'ALL' || !s.combination) && (s.day_type === 'ALL' || !s.day_type));
    }
    if (!config) {
      config = shiftSettings.find(s => s.person_type === personType);
    }
  } else if (shiftSettings && typeof shiftSettings === 'object') {
    config = shiftSettings;
  }

  if (!config) {
    config = (personType === 'CANDIDATE' ? {
      login_from: '09:00:00',
      login_to: '09:30:00',
      logout_from: isSat ? '13:30:00' : '16:30:00',
      logout_to: isSat ? '14:30:00' : '17:30:00',
      full_day_hours: isSat ? 4.00 : 7.00,
      half_day_hours: isSat ? 2.00 : 3.50,
      overtime_threshold_hours: isSat ? 4.00 : 7.00
    } : {
      login_from: '09:00:00',
      login_to: '09:30:00',
      logout_from: '17:30:00',
      logout_to: '18:30:00',
      full_day_hours: 8.00,
      half_day_hours: 4.00,
      overtime_threshold_hours: 8.00
    });
  }

  const parseMins = (t) => {
    if (!t) return null;
    if (dayjs.isDayjs(t)) return t.hour() * 60 + t.minute();
    const s = String(t);
    if (s.includes('T') || s.includes(' ')) {
      const d = dayjs(s);
      if (d.isValid()) return d.hour() * 60 + d.minute();
    }
    const p = s.split(':');
    return (parseInt(p[0], 10) || 0) * 60 + (parseInt(p[1], 10) || 0);
  };

  const inMins = parseMins(checkInTime);
  const outMins = parseMins(checkOutTime);
  const loginToMins = parseMins(config.login_to || '09:30:00');
  const logoutFromMins = parseMins(config.logout_from || (personType === 'CANDIDATE' ? '16:30:00' : '17:30:00'));
  const logoutToMins = parseMins(config.logout_to || (personType === 'CANDIDATE' ? '17:30:00' : '18:30:00'));
  const fullHours = parseFloat(config.full_day_hours || (personType === 'CANDIDATE' ? 7.0 : 8.0));
  const halfHours = parseFloat(config.half_day_hours || (personType === 'CANDIDATE' ? 3.5 : 4.0));
  const otThreshold = parseFloat(config.overtime_threshold_hours || fullHours);

  const isLate = inMins !== null && loginToMins !== null && inMins > loginToMins;
  const isEarlyLeave = outMins !== null && logoutFromMins !== null && outMins < logoutFromMins;

  if (checkInTime && !checkOutTime) {
    return isLate ? { status: 'LATE', code: 'L', label: 'L - Late' } : { status: 'PRESENT', code: 'P', label: 'P - Present' };
  }

  let durationHours = 0;
  if (inMins !== null && outMins !== null && outMins >= inMins) {
    durationHours = (outMins - inMins) / 60;
  }

  if (durationHours > (otThreshold + 0.1) || (outMins !== null && logoutToMins !== null && outMins > logoutToMins && durationHours >= fullHours)) {
    return { status: 'OVERTIME', code: 'OT', label: 'OT - Overtime' };
  }

  if (isLate) {
    if (durationHours >= fullHours) return { status: 'LATE', code: 'L', label: 'L - Late' };
    if (durationHours >= halfHours) return { status: 'HALF_DAY', code: 'HD', label: 'HD - Half Day' };
    return { status: 'ABSENT', code: 'A', label: 'A - Absent' };
  }

  if (isEarlyLeave) {
    if (durationHours >= fullHours) return { status: 'LEAVE_EARLY', code: 'LE', label: 'LE - Leave Early' };
    if (durationHours >= halfHours) return { status: 'HALF_DAY', code: 'HD', label: 'HD - Half Day' };
    return { status: 'ABSENT', code: 'A', label: 'A - Absent' };
  }

  if (durationHours >= fullHours) {
    return { status: 'PRESENT', code: 'P', label: 'P - Present' };
  } else if (durationHours >= halfHours) {
    return { status: 'HALF_DAY', code: 'HD', label: 'HD - Half Day' };
  } else {
    return { status: 'ABSENT', code: 'A', label: 'A - Absent' };
  }
};

/**
 * CSV Export utility
 */
export const exportToCSV = (data, filename) => {
  if (!data || !data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(obj => headers.map(header => {
    let val = obj[header] === null || obj[header] === undefined ? '' : String(obj[header]);
    if (val.includes(',') || val.includes('"') || val.includes('\n')) {
      val = `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  }).join(','));

  const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows].join('\n');
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `${filename}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export Colored Attendance Matrix to Styled Excel (.xls) with full color styling & badges
 */
export const exportColoredMatrixToExcel = (matrixData, dateList = [], filename = 'monthly_attendance_matrix') => {
  if (!matrixData || !matrixData.length) return;

  const isCandidate = matrixData.some(r => r.person_type === 'CANDIDATE' || r.combination);

  // Status color map for excel cells
  const getStatusStyle = (code) => {
    const raw = String(code || '-').toUpperCase();
    if (raw === 'P' || raw === 'PRESENT') {
      return { bg: '#d9f7be', color: '#237804', text: 'P' };
    } else if (raw === 'A' || raw === 'ABSENT') {
      return { bg: '#ffccc7', color: '#cf1322', text: 'A' };
    } else if (raw === 'HD' || raw === 'HALF_DAY' || raw === 'HALF DAY') {
      return { bg: '#ffe58f', color: '#d46b08', text: 'HD' };
    } else if (raw === 'OT' || raw === 'OVERTIME') {
      return { bg: '#ffd8bf', color: '#d4380d', text: 'OT' };
    } else if (raw === 'L' || raw === 'LATE') {
      return { bg: '#ffe7ba', color: '#ad4e00', text: 'L' };
    } else if (raw === 'LE' || raw === 'LEAVE_EARLY' || raw === 'LEAVE') {
      return { bg: '#b5f5ec', color: '#08979c', text: 'LE' };
    }
    return { bg: '#f5f5f5', color: '#8c8c8c', text: '-' };
  };

  let html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8">
      <style>
        table { border-collapse: collapse; font-family: Calibri, Arial, sans-serif; font-size: 11pt; }
        th { background-color: #1890ff; color: #ffffff; font-weight: bold; border: 1px solid #096dd9; padding: 8px 12px; text-align: center; }
        th.fixed-col { background-color: #0050b3; color: #ffffff; text-align: left; }
        td { border: 1px solid #d9d9d9; padding: 6px 10px; text-align: center; vertical-align: middle; }
        td.text-left { text-align: left; }
        .meta-col { background-color: #fafafa; font-weight: 500; }
      </style>
    </head>
    <body>
      <table border="1">
        <thead>
          <tr>
            <th colspan="${(isCandidate ? 4 : 3) + dateList.length + 7}" style="background-color: #002766; color: #ffffff; font-size: 14pt; padding: 12px; text-align: center;">
              MONTHLY ATTENDANCE MATRIX REPORT
            </th>
          </tr>
          <tr>
            <td colspan="${(isCandidate ? 4 : 3) + dateList.length + 7}" style="background-color: #f0f5ff; text-align: left; padding: 8px 12px;">
              <b>Status Color Legend:</b> 
              <span style="background-color: #d9f7be; color: #237804; font-weight: bold; padding: 2px 6px; border: 1px solid #b7eb8f; margin: 0 4px;">P = Present</span>
              <span style="background-color: #ffccc7; color: #cf1322; font-weight: bold; padding: 2px 6px; border: 1px solid #ffa39e; margin: 0 4px;">A = Absent</span>
              <span style="background-color: #ffe58f; color: #d46b08; font-weight: bold; padding: 2px 6px; border: 1px solid #ffd591; margin: 0 4px;">HD = Half Day</span>
              <span style="background-color: #ffd8bf; color: #d4380d; font-weight: bold; padding: 2px 6px; border: 1px solid #ffbb96; margin: 0 4px;">OT = Overtime</span>
              <span style="background-color: #ffe7ba; color: #ad4e00; font-weight: bold; padding: 2px 6px; border: 1px solid #ffd591; margin: 0 4px;">L = Late</span>
              <span style="background-color: #b5f5ec; color: #08979c; font-weight: bold; padding: 2px 6px; border: 1px solid #87e8de; margin: 0 4px;">LE = Leave Early</span>
            </td>
          </tr>
          <tr>
            <th class="fixed-col">CODE</th>
            <th class="fixed-col">NAME</th>
            <th class="fixed-col">TYPE</th>
            ${isCandidate ? '<th class="fixed-col">COMBINATION</th>' : ''}
            ${dateList.map(d => {
              const formatted = dayjs(d).format('DD-MM-YYYY');
              const day = dayjs(d).format('ddd');
              return `<th>${formatted}<br/><span style="font-size: 8pt; font-weight: normal;">${day}</span></th>`;
            }).join('')}
            <th style="background-color: #389e0d; color: #ffffff;">TOTAL P</th>
            <th style="background-color: #cf1322; color: #ffffff;">TOTAL A</th>
            <th style="background-color: #d46b08; color: #ffffff;">TOTAL HD</th>
            <th style="background-color: #d4380d; color: #ffffff;">TOTAL OT</th>
            <th style="background-color: #ad4e00; color: #ffffff;">TOTAL L</th>
            <th style="background-color: #08979c; color: #ffffff;">TOTAL LE</th>
            <th style="background-color: #0958d9; color: #ffffff;">TOTAL HRS</th>
          </tr>
        </thead>
        <tbody>
  `;

  matrixData.forEach(row => {
    const code = row.biometric_user_id || (row.code ? row.code.replace(/^(CAND-|STAFF-)/, '') : row.id);
    const name = row.name || '-';
    const type = row.person_type || '-';
    const comb = row.combination || '-';

    html += `<tr>`;
    html += `<td class="meta-col text-left">${code}</td>`;
    html += `<td class="meta-col text-left">${name}</td>`;
    html += `<td class="meta-col">${type}</td>`;
    if (isCandidate) {
      html += `<td class="meta-col" style="background-color: #e6f7ff; color: #1d39c4; font-weight: bold;">${comb}</td>`;
    }

    dateList.forEach(d => {
      const daily = row.dailyStatus?.[d];
      const codeVal = daily ? daily.code : '-';
      const style = getStatusStyle(codeVal);
      html += `<td style="background-color: ${style.bg}; color: ${style.color}; font-weight: bold; font-size: 11pt;">${style.text}</td>`;
    });

    html += `<td style="background-color: #f6ffed; color: #389e0d; font-weight: bold;">${row.summary?.presentCount || 0}</td>`;
    html += `<td style="background-color: #fff1f0; color: #cf1322; font-weight: bold;">${row.summary?.absentCount || 0}</td>`;
    html += `<td style="background-color: #fffbe6; color: #d46b08; font-weight: bold;">${row.summary?.halfDayCount || 0}</td>`;
    html += `<td style="background-color: #fff2e8; color: #d4380d; font-weight: bold;">${row.summary?.overtimeCount || 0}</td>`;
    html += `<td style="background-color: #fff7e6; color: #ad4e00; font-weight: bold;">${row.summary?.lateCount || 0}</td>`;
    html += `<td style="background-color: #e6fffb; color: #08979c; font-weight: bold;">${row.summary?.leaveEarlyCount || 0}</td>`;
    html += `<td style="background-color: #e6f7ff; color: #0958d9; font-weight: bold;">${row.summary?.totalWorkingHours || 0}</td>`;
    html += `</tr>`;
  });

  html += `
        </tbody>
      </table>
    </body>
    </html>
  `;

  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${dayjs().format('YYYYMMDD_HHmmss')}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
